import { useState, useEffect } from 'react';
import '../styles/index.css';
import { useArtworksContext } from '../hooks/useArtworksContext';
import { useAuthContext } from '../hooks/useAuthContext';
import { axiosInstance } from '../context/AuthContext';

const ArtworkForm = ({onCancel}) => {
  const { dispatch } = useArtworksContext();
  const { user } = useAuthContext();

  const [galleryId, setGalleryId] = useState('');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [artist_email, setArtistEmail] = useState('');
  const [rpId, setRpId] = useState('');
  const [price, setPrice] = useState('');
  const [image, setImage] = useState(null);
  const [imageBase64, setImageBase64] = useState('');
  const [loading, setLoading] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [isLoadingGalleries, setIsLoadingGalleries] = useState(true);
  
  const [error, setError] = useState('');
  const [emptyFields, setEmptyFields] = useState([]);

  useEffect(() => {
    const fetchUserGalleries = async () => {
      try {
        const response = await axiosInstance.get('/gallery');
        if (response.data && response.data.galleries) {
          // Filter galleries based on user's galleryId array
          const userGalleries = response.data.galleries.filter(gallery => 
            user.galleryId && user.galleryId.includes(gallery.galleryId)
          );
          setGalleries(userGalleries);
          
          if (userGalleries.length === 1) {
            setGalleryId(userGalleries[0].galleryId);
          }
        }
      } catch (error) {
        console.error('Error fetching galleries:', error);
      } finally {
        setIsLoadingGalleries(false);
      }
    };

    if (user && user.galleryId) {
      fetchUserGalleries();
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setError('You must be logged in');
      return;
    }

    if (!imageBase64) {
        setError('Please select an image to upload');
        return;
    }

    if(!rpId){
        setError('Please provide Raspberry pi ID for painting');
        return;
    }
    
    console.log('Form data before submission:', {
      galleryId,
      title,
      desc,
      artist_email,
      RaspID: rpId,
      imageBase64: imageBase64 ? 'Present' : 'Missing'
    });
    
    setLoading(true); 
    
    const artworkData = {
      galleryId,
      title,
      desc,
      artist_email,
      RaspID: rpId,
      price: Number(price),
      imageBase64
    };

    
    try {
      const res = await axiosInstance.post(
        '/artworks',
        artworkData
      );

      dispatch({ type: 'CREATE_ARTWORK', payload: res.data });
      setGalleryId('');
      setTitle('');
      setDesc('');
      setArtistEmail('');
      setPrice('');
      setImage(null);
      setImageBase64('');
      setEmptyFields([]);
      setError(null);
      onCancel()
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.error || error.message);
      setEmptyFields(error.response?.data?.emptyFields || []);
    } finally {
      setLoading(false); 
    }
  
  };

  const imageUpload = (e) => {
    const selectedImage = e.target.files[0];
    if(selectedImage){
        setImage(selectedImage);
        
        // Convert to base64
        const reader = new FileReader();
        reader.onload = () => {
          setImageBase64(reader.result);
        };
        reader.readAsDataURL(selectedImage);
    }
  };
  

  return (
    <div className="w-full max-w-80 mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="p-4 pb-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg font-sans text-blue-600">
            Add New Artwork
          </h3>
          <button
            type="button"
            className="text-gray-400 hover:text-red-500 transition-colors duration-200"
            onClick={() => onCancel()}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Gallery</label>
              {isLoadingGalleries ? (
                <div className="w-full p-2 text-xs border border-gray-300 rounded text-gray-500">
                  Loading galleries...
                </div>
              ) : (
                <select
                  className="w-full p-2 text-xs border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                  style={{appearance: 'none'}}
                  onChange={(e) => setGalleryId(e.target.value)}
                  value={galleryId}
                  required
                >
                  <option value="">Select a gallery</option>
                  {galleries.map((gallery) => (
                    <option key={gallery._id} value={gallery.galleryId}>
                      {gallery.displayName}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Camera ID</label>
              <input
                className="w-full p-2 text-xs border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                type="number"
                onChange={(e) => setRpId(e.target.value)}
                value={rpId}
                placeholder="Camera ID"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Art Title</label>
            <input
              className={`w-full p-2 text-xs border rounded focus:border-blue-500 focus:outline-none ${
                emptyFields.includes('title') ? 'border-red-500' : 'border-gray-300'
              }`}
              type="text"
              onChange={(e) => setTitle(e.target.value)}
              value={title}
              placeholder="Artwork title"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Artist Email</label>
            <input
              className="w-full p-2 text-xs border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
              type="email"
              onChange={(e) => setArtistEmail(e.target.value)}
              value={artist_email}
              placeholder="artist@example.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Purchase Price (USD)
            </label>
            <input
              className={`w-full p-2 text-xs border rounded focus:border-blue-500 focus:outline-none ${
                emptyFields.includes('price') ? 'border-red-500' : 'border-gray-300'
              }`}
              type="number"
              min="0.50"
              step="0.01"
              onChange={(e) => setPrice(e.target.value)}
              value={price}
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Image</label>
            <input
              className="w-full text-xs border border-gray-300 rounded focus:border-blue-500 focus:outline-none file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
              type='file' 
              name='file'
              onChange={imageUpload}
              accept="image/*"
            />
          </div>
          
          {imageBase64 && (
            <div className="relative">
              <img
                src={imageBase64}
                alt="Preview"
                className="w-full h-32 object-cover rounded"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="w-full p-2 text-xs border border-gray-300 rounded focus:border-blue-500 focus:outline-none resize-none"
              rows="2"
              onChange={(e) => setDesc(e.target.value)}
              value={desc}
              placeholder="Brief description..."
            />
          </div>

          <div className="flex justify-between bg-slate-800 bg-opacity-90 items-center py-3 px-4 rounded -mx-4 mt-3">
            <button
              type="button"
              onClick={() => onCancel()}
              className="px-3 py-1 text-xs text-gray-400 hover:text-white transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-200 ${
                loading ? 'cursor-not-allowed opacity-50' : ''
              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="w-3 h-3 mx-auto border-t border-white border-opacity-50 rounded-full animate-spin"></div>
              ) : (
                'Create'
              )}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded mb-0">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtworkForm;
